/**
 * Module-level helpers for Process Stage code generation & Machine Type compatibility (Rule 11 safe).
 * Remediates FB-GP49A1 (auto stepId generation without forcing manual code entry)
 * and FB-9UOTRM (stage-compatible default machine filtering so printing processes never show gluing/packing machines).
 */

const STAGE_PREFIX_MAP: Record<string, string> = {
  TRUOC_IN: "TRUOC-IN",
  IN: "IN",
  SAU_IN: "SAU-IN",
  THANH_PHAM: "TP",
};

const STAGE_ALLOWED_MACHINES: Record<string, ReadonlySet<string>> = {
  TRUOC_IN: new Set(["PAPER_CUTTER", "OTHER"]),
  IN: new Set(["OFFSET_PRINTER", "FLEXO_PRINTER", "SILK_SCREEN", "OTHER"]),
  SAU_IN: new Set([
    "COATER",
    "LAMINATOR",
    "SILK_SCREEN",
    "HOT_FOIL",
    "DIE_CUTTER",
    "OTHER",
  ]),
  THANH_PHAM: new Set([
    "DIE_CUTTER",
    "WINDOW_PATCHER",
    "FOLDER_GLUER",
    "PACKING",
    "PAPER_CUTTER",
    "OTHER",
  ]),
};

const STAGE_DEFAULT_MACHINE: Record<string, string> = {
  TRUOC_IN: "PAPER_CUTTER",
  IN: "OFFSET_PRINTER",
  SAU_IN: "LAMINATOR",
  THANH_PHAM: "DIE_CUTTER",
};

export function generateProcessStepCode(
  name: string,
  group?: string | null,
): string {
  const rawSlug = String(name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  if (!rawSlug) return "";

  const normalizedGroup = String(group || "").trim().toUpperCase();
  const prefix = STAGE_PREFIX_MAP[normalizedGroup] || "";

  if (!prefix) return rawSlug;
  if (rawSlug === prefix || rawSlug.startsWith(`${prefix}-`)) {
    return rawSlug;
  }
  return `${prefix}-${rawSlug}`;
}

export function isMachineTypeCompatibleWithStage(
  machineCode: string,
  _machineLabel?: string,
  stageCode?: string | null,
): boolean {
  const normalizedStage = String(stageCode || "").trim().toUpperCase();
  if (!normalizedStage) return true;

  const allowedSet = STAGE_ALLOWED_MACHINES[normalizedStage];
  if (!allowedSet) return true;

  const normalizedMachine = String(machineCode || "").trim().toUpperCase();
  if (!normalizedMachine) return true;

  return allowedSet.has(normalizedMachine);
}

export function getDefaultMachineTypeForStage(
  stageCode?: string | null,
): string {
  const normalizedStage = String(stageCode || "").trim().toUpperCase();
  return STAGE_DEFAULT_MACHINE[normalizedStage] || "";
}
